import { ReactElement, ReactNode } from 'react';

import {
  FolderOpenOutlined,
  Home,
  Pending,
  Share,
  Visibility,
} from '@mui/icons-material';
import { Box, Tab, Tabs } from '@mui/material';

export type MonitorDashboardScope = 'wallets' | 'following';
export type MonitorWalletTab = 'my-wallets' | 'shared' | 'pending';

interface MonitorDashboardNavigationProps {
  scope: MonitorDashboardScope;
  walletTab: MonitorWalletTab;
  isMobile: boolean;
  onScopeChange: (scope: MonitorDashboardScope) => void;
  onWalletTabChange: (tab: MonitorWalletTab) => void;
  walletContent: ReactNode;
  followingContent: ReactNode;
}

const walletTabs: {
  icon: ReactElement;
  label: string;
  value: MonitorWalletTab;
}[] = [
  { icon: <Home />, label: 'Minhas carteiras', value: 'my-wallets' },
  { icon: <Share />, label: 'Compartilhadas', value: 'shared' },
  { icon: <Pending />, label: 'Pendentes', value: 'pending' },
];

const scopeTabSx = {
  minHeight: 40,
  px: 1.5,
  border: '1px solid transparent',
  borderRadius: '8px',
  color: 'text.secondary',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  '&.Mui-selected': {
    bgcolor: '#FFFFFF',
    color: '#E66A00',
    borderColor: '#FFB570',
  },
  '&:not(.Mui-selected):hover': {
    bgcolor: '#FFF4EA',
    color: '#E66A00',
  },
  '&.Mui-focusVisible': {
    outline: '2px solid #E66A00',
    outlineOffset: '-3px',
  },
};

export function MonitorDashboardNavigation({
  scope,
  walletTab,
  isMobile,
  onScopeChange,
  onWalletTabChange,
  walletContent,
  followingContent,
}: MonitorDashboardNavigationProps) {
  return (
    <>
      <Box
        data-testid="monitor-scope-selector-wrapper"
        style={{ width: '100%', boxSizing: 'border-box' }}
        px={3}>
        <Box
          data-testid="monitor-scope-selector"
          style={{ width: '100%' }}
          sx={{
            display: 'flex',
            maxWidth: '100%',
            p: 0.5,
            bgcolor: '#F7F8FA',
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
          }}>
          <Tabs
            aria-label="Escopo do dashboard"
            value={scope}
            onChange={(_, value: MonitorDashboardScope) => onScopeChange(value)}
            variant="fullWidth"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              width: '100%',
              minHeight: 40,
              '& .MuiTabs-flexContainer': { gap: 0.5 },
            }}>
            <Tab
              id="monitor-scope-wallets-tab"
              aria-controls="monitor-scope-wallets-panel"
              icon={
                <FolderOpenOutlined
                  data-testid="monitor-scope-wallets-folder-open-icon"
                  sx={{ fontSize: '18px' }}
                />
              }
              iconPosition="start"
              label="Carteiras"
              value="wallets"
              sx={scopeTabSx}
            />
            <Tab
              id="monitor-scope-following-tab"
              aria-controls="monitor-scope-following-panel"
              icon={
                <Visibility
                  data-testid="monitor-scope-following-visibility-icon"
                  sx={{ fontSize: '18px' }}
                />
              }
              iconPosition="start"
              label="Seguindo"
              value="following"
              sx={scopeTabSx}
            />
          </Tabs>
        </Box>
      </Box>

      <Box
        role="tabpanel"
        id="monitor-scope-wallets-panel"
        aria-labelledby="monitor-scope-wallets-tab"
        hidden={scope !== 'wallets'}
        tabIndex={0}>
        <Box px={3}>
          <Tabs
            aria-label="Visões de carteiras"
            value={walletTab}
            onChange={(_, value: MonitorWalletTab) => onWalletTabChange(value)}
            indicatorColor="primary"
            textColor="primary"
            variant={isMobile ? 'fullWidth' : 'standard'}>
            {walletTabs.map((tab) => (
              <Tab
                key={tab.value}
                id={`monitor-wallet-${tab.value}-tab`}
                aria-controls={`monitor-wallet-${tab.value}-panel`}
                icon={tab.icon}
                label={tab.label}
                value={tab.value}
              />
            ))}
          </Tabs>
        </Box>
        {walletTabs.map((tab) => (
          <Box
            key={tab.value}
            role="tabpanel"
            id={`monitor-wallet-${tab.value}-panel`}
            aria-labelledby={`monitor-wallet-${tab.value}-tab`}
            hidden={scope !== 'wallets' || walletTab !== tab.value}
            tabIndex={0}>
            {scope === 'wallets' && walletTab === tab.value
              ? walletContent
              : null}
          </Box>
        ))}
      </Box>

      <Box
        role="tabpanel"
        id="monitor-scope-following-panel"
        aria-labelledby="monitor-scope-following-tab"
        hidden={scope !== 'following'}
        tabIndex={0}>
        {scope === 'following' ? followingContent : null}
      </Box>
    </>
  );
}
